import { PartialJwtPayload } from '../types/jwt-payload.type';
import { ConfigService } from './config.service';

// -----------------------------------------------------------------------------
type ApiError = {
  status: number;
  message: string;
  details?: any;
};

// -----------------------------------------------------------------------------
// eslint-disable-next-line @typescript-eslint/no-extraneous-class
class ApiService {
  // ---------------------------------------------------------------------------
  static async generateToken(
    payload: PartialJwtPayload,
    accessToken?: string
  ): Promise<{ token: string }> {
    return ApiService.post(`jwt/generate`, { ...payload }, accessToken);
  }

  // ---------------------------------------------------------------------------
  static async validateToken(
    token: string,
    accessToken?: string
  ): Promise<{
    valid: boolean;
    payload?: PartialJwtPayload;
    error?: string;
  }> {
    return ApiService.get(`jwt/verify?token=${token}`, accessToken);
  }

  // ---------------------------------------------------------------------------
  private static async get(
    endpoint: string,
    accessToken?: string
  ): Promise<any> {
    const url = `${ConfigService.getSecondaryApiUrl()}/${endpoint}`;
    const headers = ApiService.getHeaders(accessToken);
    const response = await fetch(url, {
      method: 'GET',
      headers,
    });

    return this.handleResponse(response);
  }

  // ---------------------------------------------------------------------------
  private static async post<T>(
    endpoint: string,
    body: T,
    accessToken?: string
  ): Promise<any> {
    const url = `${ConfigService.getSecondaryApiUrl()}/${endpoint}`;
    const headers = ApiService.getHeaders(accessToken);
    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
    });

    return this.handleResponse(response);
  }

  // ---------------------------------------------------------------------------
  private static async patch<T>(
    endpoint: string,
    body: T,
    accessToken?: string
  ): Promise<any> {
    const url = `${ConfigService.getSecondaryApiUrl()}/${endpoint}`;
    const headers = ApiService.getHeaders(accessToken);
    const response = await fetch(url, {
      method: 'PATCH',
      headers,
      body: JSON.stringify(body),
    });

    return this.handleResponse(response);
  }

  // ---------------------------------------------------------------------------
  private static async delete(
    endpoint: string,
    accessToken?: string
  ): Promise<any> {
    const url = `${ConfigService.getSecondaryApiUrl()}/${endpoint}`;
    const headers = ApiService.getHeaders(accessToken);
    const response = await fetch(url, {
      method: 'DELETE',
      headers,
    });

    return this.handleResponse(response);
  }

  // ---------------------------------------------------------------------------
  private static async handleResponse(response: Response): Promise<any> {
    const contentType = response.headers.get('Content-Type') || '';
    let data: any;

    if (contentType.includes('application/json')) {
      data = await response.json();
    } else {
      data = await response.text();
    }

    if (!response.ok) {
      const error: ApiError = {
        status: response.status,
        message: data?.message || response.statusText || 'An error occurred',
        details: data,
      };

      throw error;
    }

    return data;
  }

  // ---------------------------------------------------------------------------
  private static getHeaders(accessToken?: string): Headers {
    const headers = new Headers();
    headers.set('Content-Type', 'application/json');

    if (accessToken) {
      headers.set('Authorization', `Bearer ${accessToken}`);
    }

    return headers;
  }
}

export default ApiService;
